/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { isPlatformBrowser } from "@angular/common";
import { Injectable, PLATFORM_ID, effect, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import { CbRoutesUtils } from "@app/@classes/routes/cb-routes-utils";
import type { Tables } from "@app/@classes/supabase";
import { ToastService } from "@app/widgets/base/notification/toast.service";
import type { AuthChangeEvent, Session, User } from "@supabase/supabase-js";
import { Observable, Subject, from, of } from "rxjs";
import { catchError, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Supabase } from "../../@classes/supabase";
import { UsersInTenantService } from "../data/users-in-tenant.service";
import { ThemeService } from "../generic/theme.service";
import { AuthStateService } from "./auth-state.service";
import { CacheManagerService } from "./cache-manager.service";
import { FeaturesService } from "./features.service";
import { LoggerService } from "./logger.service";
import { TRPCService } from "./trpc.service";

type UsersEntity = Tables<"users">;

@Injectable({
	providedIn: "root",
})
export class AuthService extends Supabase {
	// –––––– Dependency Injections & Observables ––––––
	protected readonly usersInTenantService = inject(UsersInTenantService);
	protected readonly loggerService = inject(LoggerService);
	protected readonly featuresService = inject(FeaturesService);
	protected readonly cacheManagerService = inject(CacheManagerService);
	protected readonly router = inject(Router);
	protected readonly themeService = inject(ThemeService);
	protected readonly trpcService = inject(TRPCService);
	protected readonly toastService = inject(ToastService);
	protected readonly platformId = inject(PLATFORM_ID);
	protected readonly authStateService = inject(AuthStateService);

	// Subjects, signals and streams
	protected readonly destroy$ = new Subject<void>();

	public isLogged = signal<boolean>(false);
	public firstCheckCompleted = signal<boolean>(false);

	// Additional auth changes subject that shares the auth event, session and user info tuple
	public readonly authChanges: Subject<
		[AuthChangeEvent, Session | null, UsersEntity]
	> = new Subject();

	// –––––– Constructor & Initialization ––––––
	constructor() {
		super();

		// Check session immediately and at regular intervals (every 5 minutes)
		this.checkSession().subscribe();
		setInterval(() => this.checkSession().subscribe(), 5 * 60 * 1000);

		if (isPlatformBrowser(this.platformId)) {
			this.subscribeToAuthChanges();
		}

		effect(() => {
			const isLogged = this.isLogged();

			if (!isLogged && this.firstCheckCompleted()) {
				this.authStateService.reset();
			}
		});
	}

	// –––––– Session Management Methods –––––//

	/**
	 * Checks if there is a valid session.
	 */
	private checkSession() {
		return from(this.supabaseClient.auth.getUser()).pipe(
			map((response) => response.data.user),
			tap((user) => {
				if (user) {
					this.isLogged.set(true);
					this.authStateService.currentUserId.set(user.id);
				} else {
					this.isLogged.set(false);
				}
			}),
			catchError((err) => {
				if (err.code === "session_not_found") {
					this.signOut().subscribe();
					this.isLogged.set(false);
				} else {
					this.isLogged.set(true);
				}
				return of(null);
			}),
		);
	}

	/**
	 * Refreshes the session data from Supabase.
	 */
	public updateSession() {
		return from(this.supabaseClient.auth.getSession()).pipe(
			map((response) => response.data.session),
			tap((session) => {
				this.authStateService.currentAuthSession.set(session);
				this.authStateService.currentAppRole.set(session?.user.role || null);
			}),
			catchError((error) => this.errorHandler(error)),
		);
	}

	/**
	 * Signs out the current user locally.
	 */
	public signOut(): Observable<void> {
		return from(this.supabaseClient.auth.signOut({ scope: "local" })).pipe(
			map((response) => {
				if (response.error) throw response.error;
				this.isLogged.set(false);
			}),
			tap(() => this.cacheManagerService.removeAll()),
			catchError((error) => this.errorHandler(error)),
		);
	}

	/**
	 * Signs out the user from all other devices.
	 */
	public logoutOthers(): Observable<void> {
		return from(this.supabaseClient.auth.signOut({ scope: "others" })).pipe(
			map((response) => {
				if (response.error) throw response.error;
			}),
			catchError((error) => this.errorHandler(error)),
		);
	}

	// –––––– Authentication Methods –––––//

	/**
	 * Signs in the user with email and password.
	 * If an invitation token is provided, the invitation is processed
	 * and (if valid) the user is accepted into the tenant.
	 */
	public signIn(email: string, password: string): Observable<Session | null> {
		return from(
			this.supabaseClient.auth.signInWithPassword({
				email,
				password,
			}),
		).pipe(
			tap(() => this.firstCheckCompleted.set(true)),
			map((response) => {
				if (response.error) {
					throw response.error;
				}

				const session = response.data.session;
				if (session) {
					this.authStateService.currentAuthSession.set(session);
					this.authStateService.currentAppRole.set(session?.user.role || null);
				}

				return session;
			}),
			catchError((error) => this.errorHandler(error, false)),
		);
	}

	/**
	 * Registers a new user.
	 */
	public signUp(
		email: string,
		password: string,
		additionalUserInfo: Record<string, unknown>,
		redirectTo: string,
		data?: Record<string, unknown>,
	) {
		return from(
			this.supabaseClient.auth.signUp({
				email,
				password,
				options: {
					emailRedirectTo: redirectTo || environment.emailRedirectUrl,
					data: {
						...data,
						...additionalUserInfo,
					},
				},
			}),
		).pipe(
			map((response) => {
				if (response.error) {
					throw response.error;
				}

				return response.data;
			}),
			catchError((error) => this.errorHandler(error, false)),
		);
	}

	/**
	 * Confirms a user using an OTP code.
	 */
	public confirmUser({
		email,
		code,
	}: {
		email: string;
		code: string;
	}): Observable<Session | null> {
		return from(
			this.supabaseClient.auth.verifyOtp({
				email:
					this.authStateService.currentAuthSession()?.user.email || email || "",
				token: code,
				type: "email",
			}),
		).pipe(
			map((response) => {
				const session = response.data.session;
				if (session) {
					this.authStateService.currentAuthSession.set(session);
					this.authStateService.currentAppRole.set(session?.user.role || null);
				}

				return session;
			}),
			catchError((error) => this.errorHandler(error, false)),
		);
	}

	/**
	 * Resends the signup email.
	 */
	public sendUser({ email }: { email: string }): Observable<{
		user: User | null;
		session: Session | null;
	}> {
		return from(
			this.supabaseClient.auth.resend({
				email:
					this.authStateService.currentAuthSession()?.user.email || email || "",
				type: "signup",
			}),
		).pipe(
			map((response) => {
				if (response.error) throw response.error;
				return response.data;
			}),
			catchError((error) => this.errorHandler(error, false)),
		);
	}

	/**
	 * Updates the metadata of the current user.
	 */
	public updateMetadata(
		metadata: Record<string, unknown>,
	): Observable<User | null> {
		return from(
			this.supabaseClient.auth.updateUser({
				data: { ...metadata },
			}),
		).pipe(
			map((response) => {
				if (response.error) throw response.error;
				return response.data.user;
			}),
			catchError((error) => this.errorHandler(error)),
		);
	}

	/**
	 * Resets the user's password by sending an email.
	 */
	public resetPassword(
		email: string,
		options: { redirectTo?: string; captchaToken?: string } = {},
	): Observable<void> {
		return from(
			this.supabaseClient.auth.resetPasswordForEmail(email, options),
		).pipe(
			map((response) => {
				if (response.error) throw response.error;
			}),
			catchError((error) => this.errorHandler(error)),
		);
	}

	/**
	 * Changes the password of the currently authenticated user.
	 */
	public changePassword(newPassword: string): Observable<void> {
		return from(
			this.supabaseClient.auth.updateUser({
				password: newPassword,
			}),
		).pipe(
			map((response) => {
				if (response.error) throw response.error;
			}),
			catchError((error) => this.errorHandler(error)),
		);
	}
	public changeCollaboratorPassword(
		id: string,
		newPassword: string,
	): Observable<void> {
		return from(
			this.trpcService.client.auth.changeCollaboratorPassword.mutate({
				id,
				password: newPassword,
			}),
		).pipe(
			catchError((error) => this.errorHandler(error)),
		);
	}

	/**
	 * Verifies an OTP code.
	 */
	public verifyOtp(token: string, email: string): Observable<void> {
		return from(
			this.supabaseClient.auth.verifyOtp({
				token,
				type: "email",
				email,
			}),
		).pipe(
			map((response) => {
				if (response.error) throw response.error;
			}),
			catchError((error) => this.errorHandler(error)),
		);
	}

	// –––––– User Information Methods –––––//

	/**
	 * Fetches the current user's info from the "users" table.
	 */
	public getUserInfo(): Observable<UsersEntity | null> {
		const userId = this.authStateService.currentUserId();
		if (!userId || !this.isLogged()) return of(null);
		this.loggerService.logInfo("[Auth Service] Getting user info");

		return from(
			this.supabaseClient.from("users").select("*").eq("id", userId),
		).pipe(
			map((response) => {
				if (response.error) throw response.error;
				return response.data?.[0] ?? null;
			}),
			tap((userInfo) => {
				if (userInfo) {
					this.themeService.setTheme(userInfo.theme || "light");
					this.authStateService.currentUserInfo.set(userInfo);
				} else {
					this.authStateService.currentUserInfo.set(null);
				}
			}),
			map(() => this.authStateService.currentUserInfo()),
			catchError((error) => this.errorHandler(error)),
		);
	}

	/**
	 * Upserts additional user information.
	 */
	public updateUserAdditionalInfo(
		additionalInfo: Record<string, unknown>,
		userId?: string,
	): Observable<void> {
		const currentUserId = this.authStateService.currentUserId();
		if (!userId && !currentUserId) throw new Error("User not found");

		return from(
			this.supabaseClient
				.from("users")
				.update({
					name: additionalInfo["name"] as string | undefined,
					avatar_type: additionalInfo["avatar_type"] as
						| "text"
						| "random"
						| "image"
						| undefined,
					theme: additionalInfo["theme"] as string | undefined,
					profile_img: additionalInfo["profile_img"] as
						| string
						| null
						| undefined,
				})
				.match({
					id: userId || currentUserId,
				}),
		).pipe(
			map((response) => {
				console.log("response", response);
				if (response.error) throw response.error;
			}),
			catchError((error) => this.errorHandler(error)),
		);
	}

	// –––––– Auth Changes Subscription –––––//

	/**
	 * Subscribes to Supabase auth state changes.
	 */
	private subscribeToAuthChanges(): void {
		this.onAuthChanges()
			.pipe(takeUntil(this.destroy$))
			.subscribe(([authChange, session, user]) => {
				this.authChanges.next([authChange, session, user]);
			});
	}

	/**
	 * Monitors auth state changes and propagates state updates.
	 */
	private onAuthChanges(): Observable<
		[AuthChangeEvent, Session | null, UsersEntity]
	> {
		// Create an observable that emits on auth state change
		return new Observable<{
			event: AuthChangeEvent;
			session: Session | null;
		}>((subscriber) => {
			const { data: subscription } = this.supabaseClient.auth.onAuthStateChange(
				(event, session) => {
					subscriber.next({ event, session });
				},
			);
			// Teardown logic: unsubscribe on complete
			return () => subscription?.subscription?.unsubscribe();
		}).pipe(
			// Log the change and handle session or sign-out accordingly
			tap(({ event, session }) => {
				this.loggerService.logInfo("[Auth state change]", event, session);
				if (event === "SIGNED_IN" && session) {
					this.authStateService.currentAuthSession.set(session);
					this.authStateService.currentUserId.set(session.user.id);
					this.authStateService.currentAppRole.set(session?.user.role || null);
					this.isLogged.set(true);
				} else if (event === "SIGNED_OUT") {
					this.handleSignOut();
					this.isLogged.set(false);
				}
			}),
			// Fetch user info, then build the final tuple
			switchMap(({ event, session }) =>
				this.getUserInfo().pipe(
					map(
						(userInfo) =>
							[event, session, userInfo] as [
								AuthChangeEvent,
								Session | null,
								UsersEntity,
							],
					),
				),
			),
		);
	}

	// Separates the cleanup behavior on sign-out for clarity
	private handleSignOut(): void {
		this.isLogged.set(false);
		this.router.navigate([CbRoutesUtils.paths.auth.login.path]);
	}

	// –––––– Miscellaneous Methods –––––//

	/**
	 * Called when the page visibility changes.
	 */
	public onPageVisibilityChange(): void {
		this.checkSession().subscribe();
	}

	/**
	 * Performs cleanup before the service is destroyed.
	 */
	public onDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
